'use client';

import { useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter, usePathname } from 'next/navigation';

/**
 * OnboardingGuard
 * Redirects authenticated users to /onboarding if they haven't completed it
 * Excludes certain paths like /onboarding, /login, /api, etc.
 */
export function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    // Only check for authenticated users
    if (status !== 'authenticated' || !session?.user) {
      return;
    }

    // Paths that don't require onboarding check
    const excludedPaths = [
      '/onboarding',
      '/login',
      '/api',
      '/_next',
      '/favicon.ico',
      '/account/preferences', // Allow access to preferences
      '/account/delete', // Allow account deletion
    ];

    // Check if current path should be excluded
    const isExcluded = excludedPaths.some(excluded => 
      pathname.startsWith(excluded)
    );

    if (isExcluded) {
      return;
    }

    // Redirect to onboarding if not completed
    if (session.user.onboardingComplete === false) {
      router.push('/onboarding');
    }
  }, [status, session, pathname, router]);

  return <>{children}</>;
}

